import React from "react";
import { Modal, Form, Input, InputNumber, Row, Col } from "antd";
import { HighPremiumOrder, ProductCompanyListVO, COMMON_DICTS } from "@ifa/utils";
import { DatePickerInput } from "./RiskAssessmentDatePickerInput";
import { SelectInput } from "../../../components/form/SelectInput";

interface HighPremiumOrderModalProps {
  visible: boolean;
  onCancel: () => void;
  onSave: (values: HighPremiumOrder) => Promise<void>;
  initialValues?: HighPremiumOrder;
  companyList: ProductCompanyListVO[];
  title?: string;
}

export const HighPremiumOrderModal: React.FC<HighPremiumOrderModalProps> = ({ visible, onCancel, onSave, initialValues, companyList, title = "編輯外部保單" }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (visible) {
      if (initialValues) {
        form.setFieldsValue(initialValues);
      } else {
        form.resetFields();
        form.setFieldsValue({ currency: "HKD" });
      }
    }
  }, [visible, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      await onSave(values);
      form.resetFields();
    } catch (error) {
      console.error("表單驗證失敗:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      width="90vw"
      style={{ maxWidth: 800, top: 20 }}
      bodyStyle={{ maxHeight: "calc(90vh - 120px)", overflowY: "auto" }}
      okText="儲存"
      cancelText="取消"
      confirmLoading={loading}
      maskClosable={false}
    >
      <Form form={form} layout="vertical" style={{ paddingTop: 16 }}>
        <Row gutter={16}>
          <Col span={24} md={12}>
            <Form.Item name="orderNo" label="訂單編號" rules={[{ required: true, message: "請輸入訂單編號" }]}>
              <Input size="large" placeholder="請輸入訂單編號" />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item name="companyCode" label="保險公司" rules={[{ required: true, message: "請選擇保險公司" }]}>
              <SelectInput
                title="保險公司"
                placeholder="請選擇"
                options={companyList.map((c: any) => ({
                  label: c.companyName || c.name || c.code,
                  value: c.code,
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item name="status" label="狀態" rules={[{ required: true, message: "請輸入狀態" }]}>
              <Input size="large" placeholder="請輸入" />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item name="currency" label="貨幣" rules={[{ required: true, message: "請選擇貨幣" }]}>
              <SelectInput title="貨幣" placeholder="請選擇" options={COMMON_DICTS.currency} />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item name="annualPremium" label="年繳保費">
              <InputNumber
                size="large"
                style={{ width: "100%" }}
                placeholder="請輸入"
                min={0}
                precision={2}
                formatter={(value) => {
                  if (!value) return "";
                  return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                }}
                parser={(value) => {
                  if (!value) return 0;
                  return Number(value.replace(/,/g, ""));
                }}
              />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item name="oneTimePremium" label="一次性保費">
              <InputNumber
                size="large"
                style={{ width: "100%" }}
                placeholder="請輸入"
                min={0}
                precision={2}
                formatter={(value) => {
                  if (!value) return "";
                  return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                }}
                parser={(value) => {
                  if (!value) return 0;
                  return Number(value.replace(/,/g, ""));
                }}
              />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item name="policyNo" label="保單號碼" rules={[{ required: true, message: "請輸入保單號碼" }]}>
              <Input size="large" placeholder="請輸入" />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item name="applicationTime" label="生效日期" rules={[{ required: true, message: "請選擇生效日期" }]}>
              <DatePickerInput title="生效日期" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
